import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import i18n from '../i18n';
import { motion, AnimatePresence } from 'framer-motion';
import axios from 'axios';
import * as Icons from 'lucide-react';
import { Lock, Check, X, ChevronRight, Sparkles, BookOpen, Dumbbell, FileText } from 'lucide-react';
import { Button } from '../components/ui/button';
import { useAuth } from '../context/AuthContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CATEGORY_ORDER = [
  'fundamentals',
  'chords',
  'rhythm',
  'technique',
  'scales',
  'theory',
  'style',
  'repertoire',
];

const COLOR_CLASSES = {
  amber:  { border: 'border-amber',         bg: 'bg-amber/15',         text: 'text-amber',         gradient: 'from-amber to-orange-500' },
  blue:   { border: 'border-cyan-500',      bg: 'bg-cyan-500/15',      text: 'text-cyan-300',      gradient: 'from-blue to-cyan-400' },
  purple: { border: 'border-purple-500',    bg: 'bg-purple-500/15',    text: 'text-purple-300',    gradient: 'from-purple-500 to-pink-500' },
  green:  { border: 'border-emerald-500',   bg: 'bg-emerald-500/15',   text: 'text-emerald-300',   gradient: 'from-green-500 to-emerald-400' },
  red:    { border: 'border-red-500',       bg: 'bg-red-500/15',       text: 'text-red-300',       gradient: 'from-red-500 to-rose-400' },
};

const stateBadge = (state, t) => {
  if (state === 'mastered') return { label: t('skills.states.mastered'), Icon: Check, color: 'bg-green-500/20 text-green-400 border-green-500/40' };
  if (state === 'available') return { label: t('skills.states.available'), Icon: Sparkles, color: 'bg-amber/20 text-amber border-amber/40' };
  return { label: t('skills.states.locked'), Icon: Lock, color: 'bg-white/5 text-zinc-500 border-white/10' };
};

const ICON_ROUTE = {
  lesson: { route: '/theory', label: 'Lezione', Icon: BookOpen },
  exercise: { route: '/exercises', label: 'Esercizio', Icon: Dumbbell },
  song: { route: '/songs', label: 'Brano', Icon: FileText },
};

const SkillsPage = () => {
  const { t } = useTranslation();
  const { isAuthenticated } = useAuth();
  const lang = i18n.language?.startsWith('it') ? 'it' : 'en';

  const [skills, setSkills] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [selectedSkill, setSelectedSkill] = useState(null);

  useEffect(() => {
    const endpoint = isAuthenticated ? `${API}/skills/me` : `${API}/skills`;
    axios.get(endpoint)
      .then(r => {
        const list = r.data.map(s => isAuthenticated ? s : { ...s, state: 'available' });
        setSkills(list);
      })
      .catch(err => console.error('skills fetch error', err))
      .finally(() => setLoading(false));
  }, [isAuthenticated]);

  const skillsById = useMemo(() => {
    const m = {};
    for (const s of skills) m[s.id] = s;
    return m;
  }, [skills]);

  const visible = useMemo(() => {
    return skills.filter(s => {
      if (filter !== 'all' && s.state !== filter) return false;
      if (categoryFilter !== 'all' && s.category !== categoryFilter) return false;
      return true;
    });
  }, [skills, filter, categoryFilter]);

  const grouped = useMemo(() => {
    const g = {};
    for (const s of visible) {
      g[s.category] = g[s.category] || [];
      g[s.category].push(s);
    }
    return g;
  }, [visible]);

  const stats = useMemo(() => {
    const total = skills.length;
    const mastered = skills.filter(s => s.state === 'mastered').length;
    const available = skills.filter(s => s.state === 'available').length;
    const locked = total - mastered - available;
    return { total, mastered, available, locked };
  }, [skills]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#0A0A0A] pt-20">
        <div className="w-8 h-8 border-2 border-amber border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0A0A0A] pt-20 pb-12 px-4 sm:px-6 md:px-12" data-testid="skills-page">
      <div className="max-w-7xl mx-auto">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-8"
        >
          <span className="inline-flex items-center gap-2 mb-3 px-3 py-1 rounded-full bg-amber/10 border border-amber/30 text-amber text-xs font-bold uppercase tracking-widest">
            <Sparkles className="w-3 h-3" />
            {t('skills.tagline')}
          </span>
          <h1 className="font-heading text-4xl sm:text-5xl font-bold text-white mb-3">
            {t('skills.title')}
          </h1>
          <p className="text-zinc-400 max-w-2xl">{t('skills.subtitle')}</p>
        </motion.div>

        <div className="grid grid-cols-3 gap-3 sm:gap-4 mb-8">
          <Stat label={t('skills.stats.mastered')} value={`${stats.mastered}/${stats.total}`} color="text-green-400" />
          <Stat label={t('skills.stats.available')} value={stats.available} color="text-amber" />
          <Stat label={t('skills.stats.locked')} value={stats.locked} color="text-zinc-400" />
        </div>

        <div className="flex flex-wrap items-center gap-2 mb-6">
          {['all', 'available', 'mastered', 'locked'].map(f => (
            <button
              key={f}
              onClick={() => setFilter(f)}
              className={`px-3 py-1.5 rounded-full text-xs font-semibold uppercase tracking-wider transition-colors ${
                filter === f
                  ? 'bg-amber text-black'
                  : 'bg-white/[0.03] border border-white/10 text-zinc-400 hover:text-white'
              }`}
            >
              {t(`skills.filters.${f}`)}
            </button>
          ))}
        </div>

        <div className="flex flex-wrap items-center gap-2 mb-8">
          <button
            onClick={() => setCategoryFilter('all')}
            className={`px-3 py-1.5 rounded-full text-xs font-semibold transition-colors ${
              categoryFilter === 'all'
                ? 'bg-white text-black'
                : 'bg-white/[0.03] border border-white/10 text-zinc-400 hover:text-white'
            }`}
          >
            {t('skills.filters.allCategories')}
          </button>
          {CATEGORY_ORDER.map(cat => (
            <button
              key={cat}
              onClick={() => setCategoryFilter(cat)}
              className={`px-3 py-1.5 rounded-full text-xs font-semibold transition-colors ${
                categoryFilter === cat
                  ? 'bg-white text-black'
                  : 'bg-white/[0.03] border border-white/10 text-zinc-400 hover:text-white'
              }`}
            >
              {t(`skills.categories.${cat}`)}
            </button>
          ))}
        </div>

        <div className="space-y-10">
          {CATEGORY_ORDER.filter(c => grouped[c]?.length).map(category => (
            <section key={category}>
              <h2 className="font-heading text-2xl font-bold text-white mb-4 capitalize">
                {t(`skills.categories.${category}`)}
                <span className="text-zinc-500 text-sm font-normal ml-3">
                  {grouped[category].length} {t('skills.skillsLabel')}
                </span>
              </h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3 sm:gap-4">
                {grouped[category]
                  .sort((a, b) => {
                    const order = { beginner: 0, intermediate: 1, advanced: 2 };
                    return order[a.difficulty] - order[b.difficulty];
                  })
                  .map(s => (
                    <SkillCard
                      key={s.id}
                      skill={s}
                      lang={lang}
                      t={t}
                      onClick={() => setSelectedSkill(s)}
                    />
                  ))}
              </div>
            </section>
          ))}
        </div>

        {visible.length === 0 && (
          <div className="text-center text-zinc-500 py-16">
            {t('skills.empty')}
          </div>
        )}
      </div>

      <AnimatePresence>
        {selectedSkill && (
          <SkillModal
            skill={selectedSkill}
            skillsById={skillsById}
            onClose={() => setSelectedSkill(null)}
            lang={lang}
            t={t}
          />
        )}
      </AnimatePresence>
    </div>
  );
};

const Stat = ({ label, value, color }) => (
  <div className="bg-[#121212] border border-white/5 rounded-2xl p-4 sm:p-5">
    <div className="text-zinc-500 text-xs uppercase tracking-widest mb-1">{label}</div>
    <div className={`font-mono font-bold text-2xl sm:text-3xl ${color}`}>{value}</div>
  </div>
);

const SkillCard = ({ skill, lang, t, onClick }) => {
  const Icon = Icons[skill.icon] || Icons.Music;
  const isLocked = skill.state === 'locked';
  const isMastered = skill.state === 'mastered';
  const colorTokens = COLOR_CLASSES[skill.color] || COLOR_CLASSES.amber;
  const badge = stateBadge(skill.state, t);
  const BadgeIcon = badge.Icon;
  const name = lang === 'it' ? skill.name_it : skill.name;
  const desc = lang === 'it' ? skill.description_it : skill.description;

  return (
    <motion.button
      whileHover={{ scale: isLocked ? 1 : 1.02 }}
      onClick={onClick}
      className={`text-left bg-[#121212] border rounded-2xl p-5 transition-all ${
        isLocked
          ? 'border-white/5 opacity-50 cursor-pointer'
          : isMastered
            ? `${colorTokens.border} ${colorTokens.bg}`
            : `border-amber/30 hover:border-amber/60`
      }`}
    >
      <div className="flex items-start gap-3 mb-3">
        <div className={`w-11 h-11 rounded-xl bg-gradient-to-br ${colorTokens.gradient} flex items-center justify-center shrink-0 ${isLocked ? 'grayscale' : ''}`}>
          <Icon className="w-5 h-5 text-black" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="font-heading font-bold text-white text-sm sm:text-base leading-tight">{name}</div>
          <div className="text-zinc-500 text-xs mt-1 capitalize">{t(`skills.difficulty.${skill.difficulty}`)}</div>
        </div>
      </div>
      <p className="text-zinc-400 text-xs leading-relaxed line-clamp-2 mb-3">{desc}</p>
      <div className={`inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full border text-[10px] font-bold uppercase tracking-wider ${badge.color}`}>
        <BadgeIcon className="w-3 h-3" />
        {badge.label}
      </div>
    </motion.button>
  );
};

const SkillModal = ({ skill, skillsById, onClose, lang, t }) => {
  const Icon = Icons[skill.icon] || Icons.Music;
  const colorTokens = COLOR_CLASSES[skill.color] || COLOR_CLASSES.amber;
  const name = lang === 'it' ? skill.name_it : skill.name;
  const desc = lang === 'it' ? skill.description_it : skill.description;
  const prereqs = (skill.prerequisites || []).map(id => skillsById[id]).filter(Boolean);

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 z-50 bg-black/80 backdrop-blur-sm overflow-y-auto"
      onClick={onClose}
    >
      <div className="min-h-full flex items-center justify-center p-4">
        <motion.div
          initial={{ scale: 0.95, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          exit={{ scale: 0.95, opacity: 0 }}
          className="bg-[#0E0E10] border border-white/10 rounded-3xl max-w-2xl w-full p-6 sm:p-8 my-12"
          onClick={(e) => e.stopPropagation()}
        >
          <div className="flex items-start gap-4 mb-6">
            <div className={`w-14 h-14 rounded-2xl bg-gradient-to-br ${colorTokens.gradient} flex items-center justify-center shrink-0`}>
              <Icon className="w-7 h-7 text-black" />
            </div>
            <div className="flex-1">
              <div className="text-zinc-500 text-xs uppercase tracking-widest mb-1">
                {t(`skills.categories.${skill.category}`)} · {t(`skills.difficulty.${skill.difficulty}`)}
              </div>
              <h2 className="font-heading text-2xl sm:text-3xl font-bold text-white">{name}</h2>
            </div>
            <button onClick={onClose} className="text-zinc-500 hover:text-white shrink-0">
              <X className="w-5 h-5" />
            </button>
          </div>

          <p className="text-zinc-300 leading-relaxed mb-6">{desc}</p>

          {prereqs.length > 0 && (
            <div className="mb-6">
              <h3 className="font-heading text-sm font-bold text-zinc-400 uppercase tracking-wider mb-3">
                {t('skills.prerequisites')}
              </h3>
              <div className="space-y-2">
                {prereqs.map(p => {
                  const pName = lang === 'it' ? p.name_it : p.name;
                  return (
                    <div key={p.id} className="flex items-center gap-2 text-sm">
                      {p.state === 'mastered' ? (
                        <Check className="w-4 h-4 text-green-400 shrink-0" />
                      ) : (
                        <Lock className="w-4 h-4 text-zinc-500 shrink-0" />
                      )}
                      <span className={p.state === 'mastered' ? 'text-zinc-300' : 'text-zinc-500'}>
                        {pName}
                      </span>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {skill.linked_content?.length > 0 && (
            <div>
              <h3 className="font-heading text-sm font-bold text-zinc-400 uppercase tracking-wider mb-3">
                {t('skills.howToMaster')}
              </h3>
              <div className="space-y-2">
                {skill.linked_content.map((lc, idx) => {
                  const meta = ICON_ROUTE[lc.type];
                  if (!meta) return null;
                  const LcIcon = meta.Icon;
                  return (
                    <Link
                      key={idx}
                      to={meta.route}
                      onClick={onClose}
                      className="flex items-center gap-3 p-3 bg-white/[0.02] border border-white/10 rounded-xl hover:border-amber/40 hover:bg-amber/5 transition-colors"
                    >
                      <LcIcon className="w-4 h-4 text-amber" />
                      <span className="flex-1 text-zinc-300 text-sm font-mono truncate">
                        {t(`skills.types.${lc.type}`)} · {lc.id}
                      </span>
                      <ChevronRight className="w-4 h-4 text-zinc-500" />
                    </Link>
                  );
                })}
              </div>
            </div>
          )}

          <Button
            variant="ghost"
            onClick={onClose}
            className="w-full mt-6 text-zinc-400"
          >
            {t('common.back')}
          </Button>
        </motion.div>
      </div>
    </motion.div>
  );
};

export default SkillsPage;
